import apiService from '@/services/apiService';

// Constants for cache management
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes cache validity

// Helper to normalize listings and prepare images
const normalizeListings = (listings) => {
  return listings.map(listing => {
    // Add the first file as the main image if one isn't already set
    if (!listing.image && Array.isArray(listing.files) && listing.files.length > 0) {
      const firstFile = listing.files[0];
      listing.image = typeof firstFile === 'string' 
        ? firstFile 
        : (firstFile?.url || null);
    }
    
    // Handle image URLs array
    if (!listing.image && Array.isArray(listing.image_urls) && listing.image_urls.length > 0) {
      listing.image = listing.image_urls[0];
    }
    
    return listing;
  });
};

export default {
  namespaced: true,
  
  state: {
    listings: [],
    loading: false,
    error: null,
    lastFetch: 0,
    cacheValid: true,
  },
  
  mutations: {
    SET_LISTINGS(state, listings) {
      state.listings = listings;
      state.lastFetch = Date.now();
      state.cacheValid = true;
    },
    
    SET_LOADING(state, status) {
      state.loading = status;
    },
    
    SET_ERROR(state, error) {
      state.error = error;
    },
    
    CLEAR_ERROR(state) {
      state.error = null;
    },
    
    INVALIDATE_CACHE(state) {
      state.cacheValid = false;
    },
    
    REMOVE_LISTING(state, id) {
      state.listings = state.listings.filter(listing => listing.id !== id);
    }
  },
  
  actions: {
    /**
     * Fetch user listings with caching
     */
    async fetchListings({ commit, state }, { force = false } = {}) {
      // Check if we already have data and if it's still valid
      const cacheAge = Date.now() - state.lastFetch;
      const shouldUseCache = !force && state.listings.length > 0 && cacheAge < CACHE_TTL_MS && state.cacheValid;
      
      if (shouldUseCache) {
        console.log('Using cached user listings data', {
          listingsCount: state.listings.length,
          cacheAge: Math.round(cacheAge / 1000) + 's'
        });
        return state.listings;
      }
      
      // If no valid cache, make API request
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      try {
        const userId = this.state.auth?.user?.id;
        if (!userId) {
          throw new Error('User not authenticated');
        }
        
        const response = await apiService.userListings.getListings(userId);
        const normalizedListings = normalizeListings(response.data.data);
        
        commit('SET_LISTINGS', normalizedListings);
        return normalizedListings;
      } catch (error) {
        console.error('Error fetching user listings:', error);
        commit('SET_ERROR', error.message || 'Failed to load your listings');
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Delete a listing
     */
    async deleteListing({ commit, dispatch }, id) {
      try {
        await apiService.userListings.deleteListing(id);
        commit('REMOVE_LISTING', id);
        return true;
      } catch (error) {
        console.error('Error deleting listing:', error);
        throw error;
      }
    },
    
    /**
     * Delete multiple listings
     */
    async deleteMultipleListings({ dispatch }, ids) {
      try {
        await Promise.all(ids.map(id => dispatch('deleteListing', id)));
        return true;
      } catch (error) {
        console.error('Error deleting multiple listings:', error);
        throw error;
      }
    },
    
    /**
     * Invalidate the cache to force refresh on next fetch
     */
    invalidateCache({ commit }) {
      commit('INVALIDATE_CACHE');
    }
  },
  
  getters: {
    // All listings
    allListings: state => state.listings,
    
    // Filtered listings by status
    approvedListings: state => state.listings.filter(l => l.status === 1),
    pendingListings: state => state.listings.filter(l => l.status === 2),
    rejectedListings: state => state.listings.filter(l => l.status === 3),
    
    // Loading state
    isLoading: state => state.loading,
    
    // Error state 
    hasError: state => !!state.error,
    errorMessage: state => state.error,
    
    // Count getters for tabs
    totalListingsCount: state => state.listings.length,
    approvedListingsCount: state => state.listings.filter(l => l.status === 1).length,
    pendingListingsCount: state => state.listings.filter(l => l.status === 2).length,
    rejectedListingsCount: state => state.listings.filter(l => l.status === 3).length
  }
};